<template>
  <div
    ref="dd"
    v-click-outside="() => toggle = false"
    :class="{ open: toggle }"
    class="cselect"
    @click="toggle = !toggle"
  >
    <label
      v-if="label"
      class="form_label mt0"
      @click="toggle = !toggle"
    >
      {{ label }}
    </label>
    <button
      :disabled="disabled"
      class="cselect_top tal"
      type="button"
    >
      <slot
        :items="selectedItems"
        name="top"
      >
        <span class="p0 w400 inline-block ell">
          <slot
            v-if="slots['top-in']"
            :items="selectedItems"
            name="top-in"
          />
          <span
            v-else-if="selectedItems.length"
            class="fw700 ell"
          >
            {{ selectedLabels.join(', ') }}
          </span>
          <span
            v-else
            class="form_label m0 ell"
          >
            {{ placeholder || t('select') }}
          </span>
        </span>
      </slot>
      <span
        v-if="clearable && model.length"
        class="btn_clear"
        @click.stop="onClear"
      >
        <i class="i-times" />
      </span>
      <span class="btn_close">
        <i class="i-arrow" />
      </span>
    </button>
    <div
      v-show="toggle"
      ref="dropdown"
      :class="[{ 'upwards': openUpwards, 'left-0': left0 }]"
      class="cselect_dropdown"
    >
      <slot name="top-label" />

      <!-- Чекбокс "Выбрать все" -->
      <!--      <div class="cselect__list-item"> -->
      <!--        <input -->
      <!--            :id="'all' + uniqueId" -->
      <!--            v-model="selectAll" -->
      <!--            class="primary-color" -->
      <!--            type="checkbox" -->
      <!--            @change="toggleSelectAll" -->
      <!--        /> -->
      <!--        <label :for="'all' + uniqueId" class="fg"> -->
      <!--          {{ t('all_markets') }} -->
      <!--        </label> -->
      <!--      </div> -->

      <ul class="list cselect__list">
        <slot name="item-top">
          <li v-if="slots['item-top'] || slots['item-top-in']">
            <button
              class="btn item ell"
              type="button"
            >
              <slot name="item-top-in" />
            </button>
          </li>
        </slot>
        <li
          v-for="(item, i) in items"
          :key="i + getLabel(item)"
        >
          <slot
            :item="item"
            :on-change="() => onChange(item)"
            name="default"
          >
            <button
              :class="{ active: isSelected(item) }"
              class="btn item ell"
              type="button"
              @click.stop="onChange(item)"
            >
              <input
                :id="getModifiedString(getValue(item)) + uniqueId"
                v-model="model"
                :value="getValue(item)"
                class="primary-color"
                type="checkbox"
              >
              <label
                :for="getModifiedString(getValue(item)) + uniqueId"
                class="fg"
                @click.prevent
              >
                <slot
                  :item="item"
                  name="default-in"
                >
                  {{ translate ? t(`${translatePath || ''}${getLabel(item)}`) : getLabel(item) }}
                </slot>
              </label>
            </button>
          </slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
  items: any[] | undefined
  valueKey?: string
  labelKey?: string
  placeholder?: string
  label?: string
  clearable?: boolean
  disabled?: boolean
  translate?: boolean
  translatePath?: string
}>();
const emit = defineEmits(['onChange']);
const slots = useSlots();
const model = defineModel<any[]>('model', { default: () => [] });

const toggle = ref(false);
const dd = ref<HTMLElement>();
const dropdown = ref<HTMLElement>();
const openUpwards = ref(false);
const left0 = ref(false);
const selectAll = ref(false);

const { t } = useI18n();
const uniqueId = useId();

const getValue = (item: any) => props.valueKey ? item[props.valueKey] : item;
const getLabel = (item: any) => props.labelKey ? item[props.labelKey] : item;
const getModifiedString = (str: string) => str.replace(/\s+/g, '-').toLowerCase();

function onChange(item: any) {
  const value = getValue(item);
  const index = model.value.findIndex(i => i === value);

  if (index !== -1) {
    model.value.splice(index, 1);
  } else {
    model.value.push(value);
  }

  selectAll.value = model.value.length === props.items?.length;
  emit('onChange', model.value);
}

function onClear() {
  model.value = [];
  selectAll.value = false;
  emit('onChange', []);
}

const isSelected = (item: any) => model.value.includes(getValue(item));

const selectedItems = computed(() => model.value);
const selectedLabels = computed(() => selectedItems.value.map(label => props.items?.find(i => getValue(i) === label)?.[props.labelKey || '']));

watch(model, (newValue) => {
  selectAll.value = newValue.length === props.items?.length;
});

watch(toggle, async (newVal) => {
  if (newVal) {
    await nextTick();
    const dropdownEl = dropdown.value;
    const ddEl = dd.value;
    if (dropdownEl && ddEl) {
      const dropdownRect = dropdownEl.getBoundingClientRect();
      const ddRect = ddEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - ddRect.bottom;
      const spaceAbove = ddRect.top;
      openUpwards.value = spaceBelow < dropdownRect.height && spaceAbove > dropdownRect.height;
      left0.value = ddRect.width > 250;
    }
  }
});
</script>

<style lang="scss" scoped>
@use 'sass:color';

.cselect {
  cursor: pointer;
  overflow: hidden;
  position: relative;

  &.open {
    overflow: unset;
  }

  &_dropdown {
    max-height: 43.8rem;
    overflow-y: auto;

    input[type=checkbox] + label {
      margin: 2px 0;
    }

    &.left-0 {
      left: 0;
    }

    @media screen and (max-width: 979px) {
      position: absolute;
      left: 0;
      right: 0;
      top: 100%;
      z-index: 10;

      &.upwards {
        bottom: 100%;
        top: auto;
      }
    }
  }

  &_top {
    position: relative;
    padding-right: 0;
  }

  .btn {
    &:hover,
    &:focus {
      background: color-mix(in srgb, var(--surfaces-high-container) 97%, white);
    }
  }
}

.ell {
  // display: inline-flex;
}

.btn_clear,
.btn_close {
  margin: 0 0 0 8px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  padding: 0;

  i {
    opacity: 1;
    color: var(--surfaces-on-surface-variable);
    margin: 0;
  }

  &:hover i,
  &:focus i {
    color: color-mix(in srgb, var(--surfaces-on-surface-variable) 70%, white);
  }
}

.btn_close {
  right: 0;
  top: 50%;
}

.btn_clear {
  right: 40px;
  top: 50%;
}
</style>
